// Recent-trend panel — two views of the same recent-calls data, switched by the Per Skill / Heatmap
// toggle in the section header (`#trend-view-skill` / `#trend-view-heat` in index.html):
//   • Per Skill — SMALL-MULTIPLE SPARKLINES (one mini time series per skill). x = the rep's recent
//     calls of this scorecard group (oldest→newest), y = actual score 0–5. Each skill's expected
//     minimum is a faint dashed reference; the latest call is an emphasised dot (green on/above, red
//     below). Small multiples put TIME on x, so "is this skill improving?" is a one-glance read.
//   • Heatmap — skills (rows) × recent calls (cols); each cell's colour encodes the score vs that
//     skill's expected minimum (green at/above, red below, deeper = further), so the whole scorecard
//     history reads as one block. Good for spotting a skill that is consistently below the line.
//
// Both views share the stored { calls, targets } on the host, so the toggle (and a diamond-drag that
// moves an expected minimum) re-renders the CURRENT view without any refetch. Lean by design: hand-
// rolled inline SVG / CSS-grid, no library. Sits on the light results ground, beside Performance
// Breakdown.

import { SKILL_ORDER } from '../lib/skills.js';
import { renderHeatmap as renderHeatmap2 } from './heatmap.js';

const SVGNS = 'http://www.w3.org/2000/svg';

// Colours tuned for the LIGHT results ground; the on/below green/red mirror the GAP column.
// Colours for the HUD (dark) coaching surface: the expected-minimum reference reads teal, the
// call line + baseline lighten, and the at/below dots use the cockpit green/red.
const LINE = 'rgba(0,0,0,.40)'; // the call-to-call line (graphite, reads on the light canvas)
const EXPECTED = 'rgba(0,0,0,.34)'; // dashed expected-minimum reference (neutral grey; teal retired)
const ON_TARGET = '#2a9d57'; // latest dot / heat cell, at/above the minimum (readable green)
const BELOW = '#c23b32';   // latest dot / heat cell, below the minimum (readable red)
const FIRST = 'rgba(0,0,0,.26)';   // the oldest point, de-emphasised
const BASE = 'rgba(0,0,0,.10)';

function svgEl(tag: string, attrs: Record<string, string>): SVGElement {
  const node = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}
function d(cls: string, text?: string): HTMLDivElement {
  const node = document.createElement('div');
  node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}
/** Show integer scores as-is; keep one decimal for any legacy pre-derivation value. */
function fmtScore(v: number): string {
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}

export interface TrendCall {
  label: string;
  meta?: string; // hover detail: date · score · account · filename
  scores: Record<string, number | null>; // skill_id → actual score
}

export interface TrendOverlayOpts {
  calls: TrendCall[]; // oldest → newest, including the current call
  targets: Record<string, number | null>; // skill_id → expected minimum
}

interface Stored {
  calls: TrendCall[];
  targets: Record<string, number | null>;
}
type Mode = 'skill' | 'heat';
// Persisted on the DEVICE (localStorage), not the tab: the view choice is a UI preference, not call
// data, so it sticks across sign-out, closing the app, and importing a new call — it only changes when
// the user clicks the toggle. (Sign-out clears the diagnostic + last-view, but deliberately leaves
// device UI prefs like this and the side-nav collapse state.)
const MODE_KEY = 'studio.trendView.v1';
// The heatmap only reads well with enough columns — below this it stays locked (a few calls looks sparse).
const MIN_HEAT_CALLS = 6; // "more than 5 scored calls"
const canHeat = (calls: TrendCall[]): boolean => calls.length >= MIN_HEAT_CALLS;

function getMode(): Mode {
  try {
    return localStorage.getItem(MODE_KEY) === 'heat' ? 'heat' : 'skill';
  } catch {
    return 'skill';
  }
}
function setMode(m: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, m);
  } catch {
    /* storage unavailable (private mode) — falls back to the default each load */
  }
}

/** Render the trend panel: stores the series, wires the header toggle once, and paints the current view. */
export function renderTrendPanel(host: HTMLElement, opts: TrendOverlayOpts): void {
  (host as unknown as { __trend?: Stored }).__trend = { calls: opts.calls, targets: opts.targets };
  wireToggle(host);
  paint(host);
}

/** Live scorecard edit (diamond drag): the expected minimum moved — update targets and re-paint the
 *  current view with the stored series. Cheap. No-op if there's no series stored (e.g. trends-off note). */
export function updateTrendTargets(host: HTMLElement, targets: Record<string, number | null>): void {
  const s = (host as unknown as { __trend?: Stored }).__trend;
  if (!s || !s.calls.length) return;
  s.targets = targets;
  paint(host);
}

function paint(host: HTMLElement): void {
  const s = (host as unknown as { __trend?: Stored }).__trend;
  if (!s || !s.calls.length) return;
  const enabled = canHeat(s.calls);
  // Fall back to Per Skill when Heatmap is the stored choice but there aren't enough calls yet — without
  // clearing the choice, so it flips back to Heatmap once the rep has enough history.
  const mode: Mode = getMode() === 'heat' && enabled ? 'heat' : 'skill';
  host.innerHTML = '';
  if (mode === 'heat') renderHeatmap(host, s.calls, s.targets);
  else renderSparklines(host, s.calls, s.targets);
  syncToggle(mode, enabled);
}

// ---- Header toggle (Per Skill / Heatmap) ----------------------------------------------------------

function toggleBtns(): { skill: HTMLElement | null; heat: HTMLElement | null } {
  return { skill: document.getElementById('trend-view-skill'), heat: document.getElementById('trend-view-heat') };
}
function showLockTip(on: boolean): void {
  document.getElementById('trend-heat-tip')?.classList.toggle('is-visible', on);
}
function syncToggle(mode: Mode, enabled: boolean): void {
  const { skill, heat } = toggleBtns();
  for (const [btn, on] of [[skill, mode === 'skill'], [heat, mode === 'heat']] as [HTMLElement | null, boolean][]) {
    if (!btn) continue;
    btn.classList.toggle('is-on', on);
    btn.setAttribute('aria-selected', String(on));
  }
  if (heat) {
    heat.classList.toggle('is-locked', !enabled);
    heat.setAttribute('aria-disabled', String(!enabled));
  }
  if (enabled) showLockTip(false);
}
function wireToggle(host: HTMLElement): void {
  const { skill, heat } = toggleBtns();
  if (!skill || !heat || (skill as unknown as { __wired?: boolean }).__wired) return;
  (skill as unknown as { __wired?: boolean }).__wired = true;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  const enabledNow = (): boolean => {
    const s = (host as unknown as { __trend?: Stored }).__trend;
    return !!s && canHeat(s.calls);
  };
  const hint = (on: boolean): void => {
    clearTimeout(hideTimer);
    showLockTip(on);
  };
  skill.addEventListener('click', () => {
    setMode('skill');
    paint(host);
  });
  heat.addEventListener('click', () => {
    if (enabledNow()) {
      setMode('heat');
      paint(host);
      return;
    }
    hint(true); // locked: stay on Per Skill, explain why; auto-hide covers a tap (no hover to leave)
    hideTimer = setTimeout(() => showLockTip(false), 2600);
  });
  // reveal the "needs 6+ calls" note on hover/focus while locked; hide it when the pointer leaves
  heat.addEventListener('mouseenter', () => { if (!enabledNow()) hint(true); });
  heat.addEventListener('focus', () => { if (!enabledNow()) hint(true); });
  heat.addEventListener('mouseleave', () => hint(false));
  heat.addEventListener('blur', () => hint(false));
}

// ---- View 1: small-multiple sparklines ------------------------------------------------------------

function sparkCell(sk: { id: string; label: string }, calls: TrendCall[], targets: Record<string, number | null>): HTMLElement {
  const cell = d('spark-cell');
  const name = d('spark-name', sk.label);
  name.title = sk.label;
  cell.appendChild(name);

  const W = 160, H = 46, padL = 3, padR = 6, padT = 6, padB = 6;
  const x0 = padL, x1 = W - padR, y0 = padT, y1 = H - padB;
  const series = calls.map((c) => c.scores[sk.id]);
  const n = series.length;
  const xAt = (k: number): number => (n <= 1 ? (x0 + x1) / 2 : x0 + (k / (n - 1)) * (x1 - x0));
  const yAt = (v: number): number => y1 - (v / 5) * (y1 - y0);
  const exp = Number(targets[sk.id] ?? 0);

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, class: 'spark-svg', role: 'img' });
  svg.appendChild(svgEl('title', {}));
  (svg.lastChild as Element).textContent = `${sk.label}: expected ${exp}/5`;

  // baseline (0) + expected reference
  svg.appendChild(svgEl('line', { x1: String(x0), y1: String(yAt(0)), x2: String(x1), y2: String(yAt(0)), stroke: BASE, 'stroke-width': '1' }));
  svg.appendChild(svgEl('line', { x1: String(x0), y1: String(yAt(exp)), x2: String(x1), y2: String(yAt(exp)), stroke: EXPECTED, 'stroke-width': '1', 'stroke-dasharray': '2 3', class: 'spark-exp' }));

  // call-to-call line (skip null points as gaps)
  if (n > 1) {
    const pts = series.map((v, k) => (typeof v === 'number' ? `${xAt(k)},${yAt(v)}` : '')).filter(Boolean).join(' ');
    if (pts) svg.appendChild(svgEl('polyline', { points: pts, fill: 'none', stroke: LINE, 'stroke-width': '1.5', 'stroke-linejoin': 'round', 'stroke-linecap': 'round' }));
    if (typeof series[0] === 'number') svg.appendChild(svgEl('circle', { cx: String(xAt(0)), cy: String(yAt(series[0] as number)), r: '1.9', fill: FIRST }));
  }

  // latest call emphasised
  const last = series[n - 1];
  if (typeof last === 'number') {
    svg.appendChild(svgEl('circle', { cx: String(xAt(n - 1)), cy: String(yAt(last)), r: '3.3', fill: last < exp ? BELOW : ON_TARGET }));
  }

  cell.appendChild(svg);
  return cell;
}

function sparkLegend(): HTMLElement {
  const wrap = d('spark-legend');
  const item = (cls: string, color: string, label: string): void => {
    const s = document.createElement('span');
    const sw = document.createElement('span');
    sw.className = 'sw' + cls;
    // the expected-minimum swatch mirrors the chart's dashed reference line (stroke-dasharray 2 3)
    sw.style.background = cls.includes('dash')
      ? `repeating-linear-gradient(90deg, ${color} 0 2px, transparent 2px 5px)`
      : color;
    s.append(sw, document.createTextNode(label));
    wrap.appendChild(s);
  };
  item('', LINE, 'call-to-call');
  item(' dot', ON_TARGET, 'latest, on target');
  item(' dot', BELOW, 'latest, below');
  item(' dash', EXPECTED, 'expected minimum');
  return wrap;
}

function renderSparklines(host: HTMLElement, calls: TrendCall[], targets: Record<string, number | null>): void {
  const grid = d('spark-grid');
  for (const sk of SKILL_ORDER) grid.appendChild(sparkCell(sk, calls, targets));
  host.appendChild(grid);
  host.appendChild(sparkLegend());
}

// ---- View 2: heatmap (skills × calls) -------------------------------------------------------------

function renderHeatmap(host: HTMLElement, calls: TrendCall[], targets: Record<string, number | null>): void {
  const lastCol = calls.length - 1;
  const columns = calls.map((c, i) => ({
    label: c.label === 'This call' ? 'This' : c.label,
    title: c.meta ?? c.label,
    isCurrent: i === lastCol, // emphasise the current call
  }));
  const rows = SKILL_ORDER.map((sk) => {
    const exp = Number(targets[sk.id] ?? 0);
    return {
      label: sk.label,
      title: `${sk.label} · expected ${exp}/5`,
      cells: calls.map((c) => {
        const raw = c.scores[sk.id];
        const v = typeof raw === 'number' ? raw : null;
        return {
          value: v,
          threshold: exp,
          title: v != null ? `${sk.label} · ${c.label}: ${fmtScore(v)}/5 (expected ${exp})` : `${sk.label} · ${c.label}: not scored`,
        };
      }),
    };
  });
  host.appendChild(renderHeatmap2({ columns, rows }));
}
